<template>
  <div>
    <div class="container">
      <b-navbar class="toolbar">
        <template slot="start">
          <b-navbar-item>
            <b-input v-model="search" placeholder="Search" class="search-input"/>
            <b-button type="is-primary" @click="onSearch">Search</b-button>
          </b-navbar-item>
        </template>

        <template slot="end">
          <b-navbar-item>
            <priority-selector v-model="priority" @input="onPrioritySelected" />
          </b-navbar-item>
          <b-navbar-item>
            <status-selector v-model="status" @input="onStatusSelected" />
          </b-navbar-item>
          <b-navbar-item>
            <date-range-selector v-model="dateRange" @input="onDateRangeSelected" />
          </b-navbar-item>
        </template>
      </b-navbar>

      <documents-list
        v-if="documents.length || loading"
        :documents="documents"
        :total="total"
        :page-size="pageSize"
        :loading="loading"
        @page-change="onPageChange"
        @ordering-change="onOrderingChange"
        @selected-upc="onSelectedUpc"
        @requeued-docs="onRequeue"
      ></documents-list>

      <b-navbar v-if="documents.length" class="toolbar-bottom">
        <template slot="start">
          <b-navbar-item>
            <button v-if="busyDownload" type="button" class="button download-button" disabled>
              <b-icon icon="loading" custom-class="mdi-spin" type="is-primary"></b-icon>
              <span>Downloading XLS</span>
            </button>
            <button v-else type="button" class="button download-button" @click="downloadXls">
              <b-icon icon="file-table" type="is-primary"></b-icon>
              <span>Download as XLS</span>
            </button>
          </b-navbar-item>
        </template>
      </b-navbar>
    </div>

    <section v-if="!documents.length && !loading" class="hero is-medium is-light is-bold">
      <div class="hero-body">
        <div class="container">
          <h1 class="title">
            No documents found for selected time period
          </h1>
          <h2 class="subtitle">
            Try extending date range
          </h2>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import FileDownload from 'js-file-download'
  import formatISO from 'date-fns/formatISO'

  import autobahn from 'autobahn'
  import router from "../router"
  import { TODAY, CUSTOM } from "../const"
  import { getDocuments } from '../api'
  import { getPresetDates } from '../utils'
  import DocumentsList from '../components/DocumentsList'
  import DateRangeSelector from '../components/DateRangeSelector'
  import StatusSelector from '../components/StatusSelector'
  import PrioritySelector from '../components/PrioritySelector'

  export default {
    name: "Documents",
    components: { DateRangeSelector, StatusSelector, PrioritySelector, DocumentsList },
    data() {
      return {
        busyDownload: false,
        // Filters:
        status: null,
        search: '',
        ordering: null,
        dateRange: {
          preset: TODAY,
          start: null,
          end: null
        },
        priority: null,
        // Actual data:
        documents: [],
        total: 0,
        page: null,
        pageSize: 40,
        loading: false,

        // web socket updates:
        wssConnection: null,
        wssSession: null
      }
    },
    created () {
      // Update data from querystring
      const query = this.$route.query
      this.status = query.status || this.status
      this.search = query.search || this.search
      this.ordering = query.ordering || this.ordering
      this.page = query.page || this.page
      this.priority = query.priority || this.priority
      this.dateRange = {
        preset: query.preset || this.dateRange.preset,
        start: query.date_start ? new Date(query.date_start) : this.dateRange.start,
        end: query.date_end ? new Date(query.date_end) : this.dateRange.end,
      }
      if (query.preset === CUSTOM && !(query.date_start && query.date_end)) {
        this.dateRange = {
          preset: TODAY,
          start: null,
          end: null,
        }
      }
      // And then load from API
      this.loadData()

      // Temporarily disabling wamp (testing wamp router possible issue)
      // this.wssInitialize()
    },
    beforeDestroy() {
      if (this.wssConnection && this.wssConnection.isOpen) {
        this.wssConnection.close()
      }
    },
    methods: {
      async loadData() {
        const params = this.buildApiQueryFromData()
        this.loading = true;
        try {
          const docsData = await getDocuments(params)
          this.documents = docsData.results
          this.total = docsData.count
        } catch (e) {
          this.$buefy.toast.open({
            message: `Failed loading documents. An error occurred.`,
            type: 'is-danger'
          })
        }
        this.loading = false
      },
      onRequeue(){
        this.loadData()
      },
      onPageChange(page) {
        this.page = page;
        router.push({ query: this.buildUrlQueryFromData() })
        this.loadData();
      },
      onSearch () {
        this.page = undefined
        router.push({ query: this.buildUrlQueryFromData() })
        this.loadData();
      },
      onSelectedUpc (upc) {
        this.search = upc
        this.onSearch()
      },
      onOrderingChange(orderingParam) {
        this.ordering = orderingParam;
        router.push({query: this.buildUrlQueryFromData()});
        this.loadData();
      },
      onDateRangeSelected (dateRange) {
        this.page = undefined
        this.dateRange = dateRange
        router.push({ query: this.buildUrlQueryFromData() })
        this.loadData()
      },
      onStatusSelected () {
        this.page = undefined
        router.push({query: this.buildUrlQueryFromData()})
        this.loadData()
      },
      onPrioritySelected () {
        this.page = undefined
        router.push({query: this.buildUrlQueryFromData()})
        this.loadData()
      },
      buildUrlQueryFromData () {
        let dateStart, dateEnd
        if (this.dateRange.preset === CUSTOM) {
          dateStart = formatISO(this.dateRange.start)
          dateEnd = formatISO(this.dateRange.end)
        }
        return {
          preset: this.dateRange.preset || undefined,
          date_start: this.dateRange.preset === CUSTOM ? dateStart : undefined,
          date_end: this.dateRange.preset === CUSTOM ? dateEnd : undefined,
          status: this.status || undefined,
          search: this.search || undefined,
          ordering: this.ordering || undefined,
          priority: this.priority || undefined,
          page: this.page || undefined
        }
      },
      buildApiQueryFromData () {
        const query = {
          preset: this.dateRange.preset || undefined,
          status: this.status || undefined,
          search: this.search || undefined,
          ordering: this.ordering || undefined,
          task_priority: this.priority || undefined,
          page: this.page || undefined,
          page_size: this.pageSize
        }


        if (query.preset) {
          if (query.preset === CUSTOM) {
            query.date_start = this.dateRange.start
            query.date_end = this.dateRange.end
          } else {
            const dateRange = getPresetDates(query.preset)
            query.date_start = dateRange.dateStart
            query.date_end = dateRange.dateEnd
          }
        }
        return query
      },
      async downloadXls () {
        this.busyDownload = true
        try {
          const params = this.buildApiQueryFromData()
          const xlsData = await getDocuments(params, '.xls')
          const filename = 'approvals-report.xlsx'
          const mime = 'application/vnd.ms-excel'
          FileDownload(xlsData, filename, mime)
        } catch (e) {
          console.log(e)
        }
        this.busyDownload = false
      },
      wssInitialize () {
        const WAMP_URL = window.env.WAMP_URL || ""
        const wssHandleUpdate = this.wssHandleUpdate
        this.wssConnection = new autobahn.Connection({ url: WAMP_URL, realm: 'default' })
        this.wssConnection.onopen = session => {
          this.wssSession = session
          session.subscribe('doc_', wssHandleUpdate, { match: 'prefix' })
        }
        this.wssConnection.open()
      },
      wssHandleUpdate (args, kwargs) {
        if (kwargs && kwargs.payload) {
          try {
            const { payload } = kwargs
            const { document_id, task_status, task_worker } = payload
            const status_lookup = { failed: 'failed', running: 'processing', completed: 'processed' }
            const new_status = status_lookup[task_status]
            this.documents.map((doc) => {
              if (doc.cirrus_id === document_id) {
                if (doc.status !== new_status) {
                  doc.status = new_status
                  doc.task_worker = task_worker || ''
                }
              }
            })
          } catch (err) {
            console.log(err)
          }
        }
      }

    }
  }
</script>


<style>
  .toolbar {
    margin-bottom: 15px;
  }
  .toolbar-bottom{
    z-index: 1;
  }
  .search-input{
    margin-right: 5px;
  }
</style>
